class DotoBlogCarousel {
  constructor(root) {
    this.root = root;
    this.track = root.querySelector('[data-carousel-track]');
    this.cards = Array.from(root.querySelectorAll('[data-carousel-card]'));
    this.dotsRoot = root.querySelector('[data-carousel-dots]');
    this.prev = root.querySelector('[data-carousel-prev]');
    this.next = root.querySelector('[data-carousel-next]');
    this.autoplay = root.dataset.autoplay === 'true';
    this.pauseHover = root.dataset.pauseHover === 'true';
    this.delay = (Number.parseInt(root.dataset.autoplaySpeed || '5', 10) || 5) * 1000;
    this.timer = null;
    this.scrollRaf = null;
    this.resumeTimer = null;
    this.resizeTimer = null;

    if (!this.track || this.cards.length === 0) return;

    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onPrev = () => this.scrollPages(-1);
    this.onNext = () => this.scrollPages(1);
    this.onMouseEnter = () => this.stop();
    this.onMouseLeave = () => this.start();
    this.onInteraction = () => {
      this.stop();
      window.clearTimeout(this.resumeTimer);
      this.resumeTimer = window.setTimeout(() => this.start(), Math.max(this.delay, 2500));
    };

    this.bind();
    this.refresh();
    this.start();
  }

  bind() {
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.track.addEventListener('pointerdown', this.onInteraction, { passive: true });
    this.track.addEventListener('touchstart', this.onInteraction, { passive: true });
    this.prev?.addEventListener('click', this.onPrev);
    this.next?.addEventListener('click', this.onNext);
    window.addEventListener('resize', this.onResize, { passive: true });

    if (this.pauseHover) {
      this.root.addEventListener('mouseenter', this.onMouseEnter);
      this.root.addEventListener('mouseleave', this.onMouseLeave);
    }
  }

  get cardStep() {
    if (!this.cards[0]) return this.track.clientWidth;
    const styles = getComputedStyle(this.track);
    const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0;
    return this.cards[0].getBoundingClientRect().width + gap;
  }

  get visibleCards() {
    if (!this.cardStep) return 1;
    return Math.max(1, Math.round(this.track.clientWidth / this.cardStep));
  }

  get pageSize() {
    return this.cardStep * this.visibleCards;
  }

  get pages() {
    return Math.max(1, Math.ceil(this.track.scrollWidth / this.track.clientWidth));
  }

  get currentPage() {
    if (this.pages <= 1) return 0;
    const max = Math.max(1, this.track.scrollWidth - this.track.clientWidth);
    return Math.min(this.pages - 1, Math.round((this.track.scrollLeft / max) * (this.pages - 1)));
  }

  refresh() {
    this.buildDots();
    this.updateControls();
  }

  buildDots() {
    if (!this.dotsRoot) return;
    this.dotsRoot.innerHTML = '';

    for (let index = 0; index < this.pages; index += 1) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = '#doto-blog-carousel-dot';
      dot.setAttribute('aria-label', `Ir a página ${index + 1}`);
      dot.addEventListener('click', () => {
        const max = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
        const left = this.pages <= 1 ? 0 : (max * index) / (this.pages - 1);
        this.track.scrollTo({ left, behavior: 'smooth' });
        this.onInteraction();
      });
      this.dotsRoot.appendChild(dot);
    }

    this.updateDots();
  }

  updateDots() {
    if (!this.dotsRoot) return;
    Array.from(this.dotsRoot.children).forEach((dot, index) => {
      const active = index === this.currentPage;
      dot.classList.toggle('@active', active);
      dot.setAttribute('aria-current', active ? 'true' : 'false');
    });
  }

  updateControls() {
    const max = this.track.scrollWidth - this.track.clientWidth;
    if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
    if (this.next) this.next.disabled = this.track.scrollLeft >= max - 2;
    this.updateDots();
  }

  onScroll() {
    if (this.scrollRaf) return;
    this.scrollRaf = window.requestAnimationFrame(() => {
      this.scrollRaf = null;
      this.updateControls();
    });
  }

  onResize() {
    window.clearTimeout(this.resizeTimer);
    this.resizeTimer = window.setTimeout(() => this.refresh(), 120);
  }

  scrollPages(direction) {
    this.track.scrollBy({ left: this.pageSize * direction, behavior: 'smooth' });
    this.onInteraction();
  }

  advance() {
    const max = this.track.scrollWidth - this.track.clientWidth;
    if (max <= 2) return;

    if (this.track.scrollLeft >= max - 4) {
      this.track.scrollTo({ left: 0, behavior: 'smooth' });
    } else {
      this.track.scrollBy({ left: this.pageSize, behavior: 'smooth' });
    }
  }

  start() {
    this.stop();
    if (!this.autoplay || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.timer = window.setInterval(() => this.advance(), this.delay);
  }

  stop() {
    if (this.timer) window.clearInterval(this.timer);
    this.timer = null;
  }

  destroy() {
    this.stop();
    window.clearTimeout(this.resumeTimer);
    window.clearTimeout(this.resizeTimer);
    if (this.scrollRaf) window.cancelAnimationFrame(this.scrollRaf);
    this.track?.removeEventListener('scroll', this.onScroll);
    this.track?.removeEventListener('pointerdown', this.onInteraction);
    this.track?.removeEventListener('touchstart', this.onInteraction);
    this.prev?.removeEventListener('click', this.onPrev);
    this.next?.removeEventListener('click', this.onNext);
    window.removeEventListener('resize', this.onResize);
    this.root.removeEventListener('mouseenter', this.onMouseEnter);
    this.root.removeEventListener('mouseleave', this.onMouseLeave);
  }
}

const dotoBlogCarouselInstances = new Map();

function initDotoBlogCarousel(root) {
  if (!root || dotoBlogCarouselInstances.has(root)) return;
  dotoBlogCarouselInstances.set(root, new DotoBlogCarousel(root));
}

function destroyDotoBlogCarousel(root) {
  dotoBlogCarouselInstances.get(root)?.destroy();
  dotoBlogCarouselInstances.delete(root);
}

document.querySelectorAll('[data-doto-blog-carousel]').forEach(initDotoBlogCarousel);

document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll?.('[data-doto-blog-carousel]').forEach(initDotoBlogCarousel);
});

document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll?.('[data-doto-blog-carousel]').forEach(destroyDotoBlogCarousel);
});
